import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { catchError, map, Observable, of, switchMap, take, timeout } from 'rxjs';
import { mapCommunicationToIAnnouncement } from '../../graphql/communication/communicationMapping';
import {
  getCommunicationById,
  getCommunicationsForStudents,
  getUnreadMessageCount,
} from '../../graphql/communication/communicationQueries';
import { Communication, Data, PageInfo } from '../../graphql/models/gqlModels';
import { IAnnouncement } from '../../models/messaging';
import { IDocument, IPhoto } from '../../models/resource';
import { selectLoggedInGuardianId } from '../../store/guardian/guardian.selectors';

@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private readonly apollo = inject(Apollo);
  private readonly store = inject(Store);
  year = new Date().getFullYear();
  startDate = `${this.year}-01-01`;

  getAppCommunications(
    studentIds: string[],
    offset = 0,
  ): Observable<{ items: IAnnouncement[]; pageInfo: PageInfo | null }> {
    if (!studentIds || studentIds.length === 0) {
      console.error('No student IDs provided to getAppCommunications');
      return of({ items: [], pageInfo: null });
    }

    const query = gql`
      ${getCommunicationsForStudents()}
    `;

    return this.store.select(selectLoggedInGuardianId).pipe(
      take(1),
      switchMap((guardianId) => {
        if (!guardianId) {
          console.error('No logged in guardian ID found');
          return of({ items: [], pageInfo: null });
        }

        return this.apollo
          .query<Data>({
            query,
            variables: {
              studentIds,
              guardianId,
              year: this.year,
              startDate: this.startDate,
              limit: 50,
              offset,
            },
            errorPolicy: 'all',
            fetchPolicy: 'network-only',
          })
          .pipe(
            timeout(15000),
            map((response) => {
              if (response.errors && response.errors.length > 0) {
                console.warn('GraphQL errors for communications (but checking for usable data):', response.errors);
              }

              const communications = response.data.communicationsSearch?.content;
              const pageInfo = response.data.communicationsSearch?.pageInfo ?? null;

              if (!communications || communications.length === 0) {
                return { items: [], pageInfo };
              }

              const commMap = new Map<string, IAnnouncement>();
              const duplicateIds = new Set<string>();

              communications.forEach((communication: Communication) => {
                const commIdStr = String(communication.id);
                if (commMap.has(commIdStr)) {
                  duplicateIds.add(commIdStr);
                  return;
                }

                try {
                  commMap.set(commIdStr, mapCommunicationToIAnnouncement(communication, communication.studentId ?? ''));
                } catch (mappingError) {
                  console.error(`Error mapping communication ${communication.id}:`, mappingError);
                }
              });

              duplicateIds.forEach((id) => {
                const comm = commMap.get(id);
                if (comm?.avatar) comm.avatar.id = '';
              });

              const items = Array.from(commMap.values()).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
              return { items, pageInfo };
            }),
            catchError((error) => {
              if (error.name === 'TimeoutError') {
                console.warn('Timeout occurred fetching communications after 15 seconds');
              } else {
                console.error('Error fetching communications:', error);
              }
              return of({ items: [], pageInfo: null });
            }),
          );
      }),
    );
  }

  updateReadStatus(communicationId: string, guardianSynergyId: string): Observable<boolean> {
    const mutation = gql`
      mutation MarkCommunicationAsRead($communicationId: Int!, $guardianId: String!) {
        markCommunicationAsRead(input: { communicationId: $communicationId, guardianId: $guardianId }) {
          dateCreated
          communicationId
          guardianSynergyId
          id
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation,
        variables: {
          communicationId: parseInt(communicationId, 10),
          guardianId: guardianSynergyId,
        },
        errorPolicy: 'all',
      })
      .pipe(
        timeout(10000), // 10 second timeout
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            console.warn(`GraphQL errors marking communication ${communicationId} as read:`, response.errors);
            return false;
          }
          return true;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            console.warn(`Timeout occurred marking communication ${communicationId} as read after 10 seconds`);
          } else {
            console.error(`Error marking communication ${communicationId} as read:`, error);
          }
          return of(false); // Return false for failed mutations
        }),
      );
  }

  getGuardianNotificationSettings(guardianId: string): Observable<{ messaging: boolean; app: boolean } | null> {
    const query = gql`
      query GetGuardianNotificationSettings($guardianId: String!) {
        getGuardianNotificationSettings(guardianId: $guardianId) {
          guardianId
          messaging
          app
          dateUpdated
        }
      }
    `;

    return this.apollo
      .query<{ getGuardianNotificationSettings: { messaging: boolean; app: boolean } | null }>({
        query,
        variables: { guardianId },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })
      .pipe(
        timeout(10000), // 10 second timeout
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            console.warn(`GraphQL errors fetching notification settings for guardian ${guardianId}:`, response.errors);
            return null;
          }
          return response.data.getGuardianNotificationSettings;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            console.warn(`Timeout occurred fetching notification settings for guardian ${guardianId} after 10 seconds`);
          } else {
            console.error(`Error fetching notification settings for guardian ${guardianId}:`, error);
          }
          return of(null); // Return null for failed queries
        }),
      );
  }

  setGuardianNotificationSettings(guardianId: string, messaging: boolean, app: boolean): Observable<boolean> {
    const mutation = gql`
      mutation UpdateGuardianNotificationSettings($guardianId: String!, $messaging: Boolean!, $app: Boolean!) {
        updateGuardianNotificationSettings(input: { guardianId: $guardianId, messaging: $messaging, app: $app }) {
          guardianId
          messaging
          app
          dateUpdated
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation,
        variables: {
          guardianId,
          messaging,
          app,
        },
        errorPolicy: 'all',
      })
      .pipe(
        timeout(10000), // 10 second timeout
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            console.warn(`GraphQL errors updating notification settings for guardian ${guardianId}:`, response.errors);
            return false;
          }
          return true;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            console.warn(`Timeout occurred updating notification settings for guardian ${guardianId} after 10 seconds`);
          } else {
            console.error(`Error updating notification settings for guardian ${guardianId}:`, error);
          }
          return of(false); // Return false for failed mutations
        }),
      );
  }

  getEmailBody(id: string): Observable<{
    body: string;
    sizeWarning?: boolean;
    sizeKb?: number;
    documents: IDocument[];
    photos: IPhoto[];
  }> {
    return this.getCommunicationById(id).pipe(
      map((comm) => {
        const html = comm?.emails?.[0]?.html ?? '';
        const sizeKb = Math.round(new Blob([html]).size / 1024);

        const attachments = comm?.attachments ?? [];
        const baseUrl = `/myhub-api/api/v1/communication/${comm?.id}/file`;
        const imageExts = new Set(['.jpg', '.jpeg', '.png', '.gif']);

        const documents: IDocument[] = attachments
          .filter(
            (a) =>
              a.file.extension === '.pdf' ||
              !(
                a.file.extension === '.jpg' ||
                a.file.extension === '.jpeg' ||
                a.file.extension === '.png' ||
                a.file.extension === '.gif'
              ),
          )
          .map((a) => ({
            id: a.file.id,
            name: a.file.name,
            type: 'Document' as const,
            timestamp: new Date(),
            documentUrl: `${baseUrl}/${a.file.id}/download`,
          }));

        const photos: IPhoto[] = attachments
          .filter((a) => imageExts.has(a.file.extension))
          .map((a) => ({
            id: a.file.id,
            name: a.file.name,
            type: 'Photo' as const,
            timestamp: new Date(),
            imageUrl: `${baseUrl}/${a.file.id}/download`,
          }));

        return { body: html, sizeKb, sizeWarning: sizeKb > 500, documents, photos };
      }),
    );
  }

  markAllAsRead(studentId: string[], startDate: string = this.startDate): Observable<boolean> {
    return this.store.select(selectLoggedInGuardianId).pipe(
      take(1),
      switchMap((guardianId) => {
        if (!guardianId) {
          console.error('guardianId is null or undefined');
          return of(false);
        }

        const mutation = gql`
          mutation MarkAllAsRead($guardianId: String!, $studentId: [String!]!, $startDate: String!) {
            markAllAsRead(input: { guardianId: $guardianId, studentId: $studentId, startDate: $startDate })
          }
        `;

        return this.apollo
          .mutate({
            mutation,
            variables: {
              guardianId,
              studentId,
              startDate,
            },
            errorPolicy: 'all',
          })
          .pipe(
            timeout(10000),
            map((response) => {
              if (response.errors && response.errors.length > 0) {
                console.error(response.errors);
                return false;
              }
              return true;
            }),
            catchError((error) => {
              if (error.name === 'TimeoutError') {
                console.warn('Timeout occurred marking all communications as read after 10 seconds');
              } else {
                console.error('Error marking all communications as read:', error);
              }
              return of(false);
            }),
          );
      }),
    );
  }

  /**
   * Returns the total number of unread messages for a guardian across the given students
   * from the provided start date onward.
   */
  getUnreadMessageCount(
    guardianId: string,
    studentIds: string[],
    startDate: string = this.startDate,
  ): Observable<number> {
    const query = gql`
      ${getUnreadMessageCount()}
    `;

    return this.apollo
      .query<Data>({
        query,
        variables: { guardianId, studentIds, startDate },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })
      .pipe(
        timeout(10000),
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            console.warn('GraphQL errors fetching unread message count:', response.errors);
          }
          return response.data.unreadMessageCount ?? 0;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            console.warn('Timeout occurred fetching unread message count after 10 seconds');
          } else {
            console.error('Error fetching unread message count:', error);
          }
          return of(0);
        }),
      );
  }

  /**
   * Fetches a single communication by ID including its full email HTML body.
   * Use this to lazy-load the body when a user opens an announcement.
   */
  getCommunicationById(id: string): Observable<Communication | null> {
    const query = gql`
      ${getCommunicationById()}
    `;

    return this.apollo
      .query<Data>({
        query,
        variables: { id },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })
      .pipe(
        timeout(20000), // 20s — email HTML bodies can be large
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            console.warn(`GraphQL errors fetching communication ${id}:`, response.errors);
          }
          return response.data.communication ?? null;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            console.warn(`Timeout occurred fetching communication ${id} after 60 seconds`);
          } else {
            console.error(`Error fetching communication ${id}:`, error);
          }
          return of(null);
        }),
      );
  }
}
